import React, { useEffect, useMemo, useRef, useState } from "react";
import { FallingLines } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Switch from "react-switch";
import { toast } from "react-toastify";
import Background1 from "../../assets/images/Background1.jpg";
import Header from "../../components/Header/Header";
import ScrollToTop from "../../components/Hooks/ScrollToTop";
import axios from "../../components/Hooks/axios";
import EnablePollContainer from "../Post/EnablePollContainer";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../Post/Post.module.scss";
import CustomCalendar from "../../components/calender/CustomCalendar ";
import { FiSearch } from "react-icons/fi";
import Avatar from "../../components/Avatar/Avatar";
import { setCustomPollKey, setCustomPollShow, setPollEnable } from "../../redux/slices/tempSlice";
import { IoChevronBackOutline, IoChevronForwardOutline, IoAlertCircle } from "react-icons/io5";
import { validateMediaFile, getFriendlyUploadErrorMessage } from "../../utils/fileValidation";

// Global cache for stable blob URLs to prevent video/image buffering
const blobCache = new WeakMap();
const getStableUrl = (file) => {
	if (!file) return null;
	if (typeof file === "string") return file;
	if (Array.isArray(file)) {
		const first = file[0];
		return getStableUrl(first);
	}
	if (typeof file === "object" && file.url) return file.url;
	if (file instanceof Blob || file instanceof File) {
		if (!blobCache.has(file)) {
			blobCache.set(file, URL.createObjectURL(file));
		}
		return blobCache.get(file);
	}
	return null;
};

const normalizeMediaItems = (media) => {
	if (!media) return [];
	if (Array.isArray(media)) return media.flatMap(normalizeMediaItems);
	return [media];
};

const getMediaType = (media) => {
	if (!media) return "image";
	if (typeof media === "string") {
		return /\.(mp4|mov|webm|ogg)(\?|$)/i.test(media) ? "video" : "image";
	}
	if (media.type?.startsWith("video")) return "video";
	return "image";
};


const UploadPost = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const data = useLocation();
	const { id } = useParams();
	const { userId } = useSelector((state) => state.auth);
	const { pollObj } = useSelector(state => state.temp);
	const [pollOption, setPollOption] = useState("no");
	const [customPoll, setCustomPoll] = useState(data?.state?.customPoll || false);
	const [showResults, setShowResults] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const [openCal, setOpenCal] = useState(false);
	const [customDays, setCustomDays] = useState(null);

	const [fileData, setFileData] = useState(data?.state?.fileData);
	const [fileType, setFileType] = useState(data?.state?.fileType);
	const imageRef = useRef(null);
	const [enablePoll, setEnablePoll] = useState(data?.state?.enablePoll || false);
	const [pollResult, setPollResult] = useState(true);
	const [timer, setTimer] = useState(data?.state?.timer || "0");
	const [caption, setCaption] = useState(data?.state?.caption === undefined ? "" : data?.state?.caption);
	const [tag, setTag] = useState(data?.state?.tag || []);
	const [tagText, setTagText] = useState("");
	const [btnDisable, setBtnDisable] = useState(false);
	const textRef = useRef();

	const [followers, setFollowers] = useState([]);
	const [filteredFollowers, setFilteredFollowers] = useState([]);
	const [showMentions, setShowMentions] = useState(false);
	const [cursorPos, setCursorPos] = useState(0);
	const [isFollowersLoading, setIsFollowersLoading] = useState(false);
	const [activeMediaIndex, setActiveMediaIndex] = useState(0);
	const [uploadPercentage, setUploadPercentage] = useState(0);
	const [isTextPost, setIsTextPost] = useState(data?.state?.isTextPost || false);
	const [originalText, setOriginalText] = useState(data?.state?.originalText || "");
	const [originalBgColor, setOriginalBgColor] = useState(data?.state?.originalBgColor || null);

	const previewItems = useMemo(() => {
		return normalizeMediaItems(fileData).map((media) => ({
			media,
			url: getStableUrl(media),
			type: getMediaType(media),
		}));
	}, [fileData]);
	const hasMultipleMedia = previewItems.length > 1;

	useEffect(() => {
		const fetchFollowers = async () => {
			setIsFollowersLoading(true);
			try {
				const { data } = await axios.get(`/auth/follower-following`);
				console.log("Followers data received:", data);
				const followersData = data.data.followersData || [];
				setFollowers(followersData);
			} catch (error) {
				console.error("Failed to fetch followers", error);
			} finally {
				setIsFollowersLoading(false);
			}
		};
		fetchFollowers();
	}, []);

	const [isEdit, setIsEdit] = useState(false);

	useEffect(() => {
		if (id) {
			setIsEdit(true);
			const fetchPost = async () => {
				try {
					const { data: post } = await axios.get(`/post/getPost/${id}`);
					setCaption(post.caption || "");
					setEnablePoll(post.Enabledpoll);
					setPollResult(post.ShowPollResults);
					setTimer(post.pollDuration || "0");
					setFileType(post.mediaType);
					setFileData(post.mediaUrl);

					if (post.tags && Array.isArray(post.tags)) {
						setTag(post.tags);
						setTagText(post.tags.map(t => `#${t}`).join(" "));
					}

					if (post.privacy) {
						if (post.privacy.like_view) setLikeViewPrivacy(post.privacy.like_view);
						if (post.privacy.comments) setCommentPrivacy(post.privacy.comments);
						if (post.privacy.sharing) setSharingPrivacy(post.privacy.sharing);
					}

					if (post.Enabledpoll) {
						if (post.customPollEnabled) {
							setCustomPoll(true);
							dispatch(setCustomPollKey(post.customPollData || []));
						} else {
							setPollOption(post.pollOption || "no");
						}
					}
				} catch (error) {
					console.error("Failed to fetch post", error);
					toast.error("Failed to load post data");
				}
			};
			fetchPost();
		}
	}, [id, dispatch]);

	const handleCaptionChange = (e) => {
		const val = e.target.value;
		const selectionStart = e.target.selectionStart;
		setCursorPos(selectionStart);

		const words = val.split(/\s+/);
		if (words.filter(w => w.trim() !== "").length <= 32) {
			setCaption(val);
		} else {
			toast.info("Limit Exceeded!!");
			return;
		}

		// Mention detection
		const textBeforeCursor = val.substring(0, selectionStart);
		const lastAtSymbol = textBeforeCursor.lastIndexOf("@");
		if (lastAtSymbol !== -1 && !textBeforeCursor.substring(lastAtSymbol).includes(" ")) {
			const query = textBeforeCursor.substring(lastAtSymbol + 1);
			const filtered = followers.filter(f =>
				f.username?.toLowerCase().includes(query.toLowerCase()) ||
				f.displayName?.toLowerCase().includes(query.toLowerCase())
			);
			setFilteredFollowers(filtered);
			setShowMentions(true);
		} else {
			setShowMentions(false);
		}
	};

	const insertMention = (user) => {
		const textBeforeAt = caption.substring(0, caption.lastIndexOf("@", cursorPos - 1));
		const textAfterMention = caption.substring(cursorPos);
		const newCaption = `${textBeforeAt}@${user.username} ${textAfterMention}`;
		setCaption(newCaption);
		setShowMentions(false);
	};

	const [likeViewPrivacy, setLikeViewPrivacy] = useState(data?.state?.likeViewPrivacy || { option: "everyone", specific_users: [] });
	const [commentPrivacy, setCommentPrivacy] = useState(data?.state?.commentPrivacy || { option: "everyone", specific_users: [] });
	const [sharingPrivacy, setSharingPrivacy] = useState(data?.state?.sharingPrivacy || { option: "everyone", specific_users: [] });
	const [showCommentOptions, setShowCommentOptions] = useState(false);
	const [showLikeViewOptions, setShowLikeViewOptions] = useState(false);
	const [showSharingOptions, setShowSharingOptions] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const isPollLocked = !enablePoll;

	const handleUserSearch = (query) => {
		setSearchTerm(query);
		if (query.trim() === "") {
			setSearchResults([]);
			return;
		}
		const filtered = followers.filter(f =>
			f.username?.toLowerCase().includes(query.toLowerCase()) ||
			f.displayName?.toLowerCase().includes(query.toLowerCase())
		);
		setSearchResults(filtered);
	};

	const toggleUserInPrivacy = (privacy, setPrivacy, user) => {
		const isSelected = privacy.specific_users.some(u => (u._id || u) === user._id);
		let newUsers;
		if (isSelected) {
			newUsers = privacy.specific_users.filter(u => (u._id || u) !== user._id);
		} else {
			newUsers = [...privacy.specific_users, user._id];
		}
		setPrivacy({ ...privacy, specific_users: newUsers });
	};

	const openFullEditor = async (sourceMedia = fileData) => {
		if (isTextPost) {
			navigate("/UploadPost2", {
				state: {
					isTextPost: true,
					originalText,
					originalBgColor,
					caption,
				},
			});
			return;
		}

		const mediaItems = normalizeMediaItems(sourceMedia);
		if (mediaItems.length === 0) {
			toast.warn("Please Select image/video !!");
			return;
		}

		const files = [];

		for (let index = 0; index < mediaItems.length; index += 1) {
			const media = mediaItems[index];
			if (media instanceof File || media instanceof Blob) {
				files.push(media);
				continue;
			}

			if (typeof media === "string") {
				try {
					const response = await fetch(media);
					const blob = await response.blob();
					const extension = blob.type.includes("video") ? "mp4" : "jpg";
					files.push(new File([blob], `media-${index + 1}.${extension}`, { type: blob.type || "image/jpeg" }));
				} catch (error) {
					console.error("Failed to convert media URL to file", error);
					toast.error("Unable to open the image editor for this media.");
					return;
				}
			}
		}

		navigate("/UploadPost2", {
			state: {
				fileData: files.length === 1 ? files[0] : files,
				caption,
			},
		});
	};

	const handelUpload = () => {
		const filesToUpload = previewItems.map((item) => item.media).filter((item) => item instanceof Blob);
		if (!isEdit && filesToUpload.length === 0) return toast.warn("Please Select image/video !!");

		for (const file of filesToUpload) {
			const validation = validateMediaFile(file);
			if (!validation.valid) {
				toast.error(validation.error);
				return;
			}
		}

		setBtnDisable(true);
		const toastId = toast.loading(isEdit ? "Updating..." : "Uploading...");

		const formData = new FormData();

		formData.append("userId", userId);
		formData.append("mediaType", fileType);
		filesToUpload.forEach((file) => {
			formData.append("mediaUrl", file);
		});
		formData.append("Enabledpoll", enablePoll ? "true" : "false");
		formData.append("ShowPollResults", pollResult ? "true" : "false");
		formData.append("caption", caption);
		formData.append("pollDuration", timer);
		const postPrivacy = {
			like_view: likeViewPrivacy,
			comments: commentPrivacy,
			sharing: sharingPrivacy
		};

		formData.append("privacy", JSON.stringify(postPrivacy));
		formData.append("tag_privacy", `{"everyone":true,"followers":false,"no_one":false}`);
		formData.append("tags", JSON.stringify(tag));
		formData.append("post_hide", "[]");
		formData.append("tagPeople", "[]");
		formData.append("pollOption", pollOption);
		formData.append("customPollEnabled", (enablePoll && customPoll && pollObj.pollKeys.length > 0) ? "true" : "false");
		formData.append("customPollData", JSON.stringify(pollObj.pollKeys));

		const apiCall = isEdit
			? axios.patch(`/post/update?postId=${id}`, formData)
			: axios.post("/post/", formData, {
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					setUploadPercentage(percentCompleted);
				},
			});

		apiCall
			.then(({ data }) => {
				toast.update(toastId, { render: data.message, type: "success", isLoading: false, autoClose: 2000 });
				setBtnDisable(false);
				dispatch(setPollEnable(false));
				dispatch(setCustomPollKey([]));
				dispatch(setCustomPollShow({ enableCustomPoll: false, customPollPopupShow: false }));
				navigate("/Home");
			})
			.catch((err) => {
				setBtnDisable(false);
				const errorMsg = getFriendlyUploadErrorMessage(err, "Something went wrong while uploading");
				toast.update(toastId, { render: errorMsg, type: "error", isLoading: false, autoClose: 3000 });
				console.error(err);
			});
	};

	useEffect(() => {
		if (enablePoll) setTimer("1");
		else {
			setTimer("0");
			setPollResult(false);
		}
	}, [enablePoll]);


	useEffect(() => {
		if (fileData) {
			const firstMedia = Array.isArray(fileData) ? fileData[0] : fileData;
			if (firstMedia instanceof File || firstMedia instanceof Blob) {
				if (firstMedia.type.includes("video")) {
					setFileType("video");
				} else if (firstMedia.type.includes("image")) {
					setFileType("image");
				}
			} else if (typeof fileData === "string") {
				// Type is already set from fetchPost in edit mode
			}
		}
	}, [fileData]);

	useEffect(() => {
		setActiveMediaIndex((current) => Math.min(current, Math.max(previewItems.length - 1, 0)));
	}, [previewItems.length]);

	const handleMediaChange = async (e) => {
		const selectedFiles = Array.from(e.target.files || []);
		e.target.value = null;

		if (selectedFiles.length === 0) return;

		for (const file of selectedFiles) {
			const validation = validateMediaFile(file);
			if (!validation.valid) {
				toast.error(validation.error);
				return;
			}
		}

		const hasImage = selectedFiles.some((file) => file.type.startsWith("image/"));
		const hasVideo = selectedFiles.some((file) => file.type.startsWith("video/"));

		if (hasImage && hasVideo) {
			toast.info("Please upload only images or only videos at a time.");
			return;
		}

		if (!isEdit) {
			navigate("/UploadPost2", {
				state: {
					fileData: selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles,
					caption: "",
				},
			});
			return;
		}

		setFileData(selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles);
		setFileType(hasVideo ? "video" : "image");
		setActiveMediaIndex(0);
	};

	useEffect(() => {
		if (selectedDate) {
			const today = new Date();
			const selected = new Date(selectedDate);

			const diffTime = selected - today;
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

			if (diffDays > 0) {
				setCustomDays(diffDays);
				setTimer(diffDays.toString());
			}
		}
	}, [selectedDate]);
	return (
		<>
			<div className="App">
				<div className="MainContainer">
					<ScrollToTop />
					<Header />
					<div className={`${styles.Container} ${styles.Post}`}>
						{/* <SmallHeader /> */}
						<div className={`${styles.EditPostBody} ${(showCommentOptions || showLikeViewOptions || showSharingOptions) ? styles.expanded : ""}`}>
							<div className={styles.EditPostBodyLeft}>
								<div className={styles.EditPostBox}>
									<div className={styles.ImageContainer} onClick={() => imageRef?.current?.click()}>
										{previewItems.length > 0 ? (
											<>
												{hasMultipleMedia && (
													<button
														type="button"
														className={`${styles.CarouselArrow} ${styles.CarouselArrowPrev}`}
														onClick={(event) => {
															event.stopPropagation();
															setActiveMediaIndex((current) => Math.max(current - 1, 0));
														}}
														disabled={activeMediaIndex === 0}>
														<IoChevronBackOutline />
													</button>
												)}

												<div className={styles.MediaViewport} onClick={(event) => event.stopPropagation()}>
													<div
														className={styles.MediaTrack}
														style={{ transform: `translateX(-${activeMediaIndex * 100}%)` }}>
														{previewItems.map((item, index) => (
															<div className={styles.MediaSlide} key={`${item.url || index}-${index}`}>
																{item.type === "video" ? (
																	<video src={item.url} controls playsInline />
																) : (
																	<img src={item.url || Background1} alt={`Selected media ${index + 1}`} />
																)}
															</div>
														))}
													</div>
												</div>

												{hasMultipleMedia && (
													<button
														type="button"
														className={`${styles.CarouselArrow} ${styles.CarouselArrowNext}`}
														onClick={(event) => {
															event.stopPropagation();
															setActiveMediaIndex((current) => Math.min(current + 1, previewItems.length - 1));
														}}
														disabled={activeMediaIndex >= previewItems.length - 1}>
														<IoChevronForwardOutline />
													</button>
												)}

												{hasMultipleMedia && (
													<div className={styles.MediaCounter}>
														<span>{activeMediaIndex + 1}</span>
														<span>/</span>
														<span>{previewItems.length}</span>
													</div>
												)}
											</>
										) : (
											<div className={styles.EmptyMediaState}>
												<h3>Tap to attach photos or video</h3>
												<p>Choose from your computer to build the post preview.</p>
											</div>
										)}
										<button
											type="button"
											className={styles.EditPostButton}
											onClick={(e) => {
												e.stopPropagation();
												openFullEditor();
											}}
											disabled={previewItems.length === 0 || previewItems[activeMediaIndex]?.type === "video"}>
											Edit Post
										</button>
										<input
											type="file"
											ref={imageRef}
											accept="image/*,video/*"
											multiple
											style={{ display: "none" }}
											onChange={handleMediaChange}
										/>
									</div>
								</div>

								{enablePoll && (
									<>
										<div className={styles.AnalyticsSection}>
											<h3>SEE ANALYTICS</h3>
											{customPoll && pollObj.pollKeys.length > 0 ? (
												pollObj.pollKeys.map((key, i) => (
													<div className={styles.AnalyticsRow} key={i}>
														<span className={styles.OptionLabel}>{key}</span>
														<div className={styles.Track}><div className={styles.Bar} style={{ width: "0%" }}></div></div>
														<div className={styles.Percent}>0%</div>
													</div>
												))
											) : (
												<>
													<div className={styles.AnalyticsRow}>
														<span className={styles.OptionLabel}>Yes</span>
														<div className={styles.Track}><div className={styles.Bar} style={{ width: "0%" }}></div></div>
														<div className={styles.Percent}>0%</div>
													</div>
													<div className={styles.AnalyticsRow}>
														<span className={styles.OptionLabel}>No</span>
														<div className={styles.Track}><div className={styles.Bar} style={{ width: "0%" }}></div></div>
														<div className={styles.Percent}>0%</div>
													</div>
												</>
											)}
										</div>

										<div className={styles.PollTimerBanner}>
											<div className={styles.Icon}><IoAlertCircle /></div>
											<p>Your poll will end in {timer === "0" ? "2hrs" : `${timer} days`}</p>
										</div>
									</>
								)}
							</div>

							<div className={styles.EditPostBodyRight}>

								<div className={styles.RightInner}>
									{/* <EnablePollContainer /> */}

									<div className={styles.LeftBottom}>
										<div className={styles.EnablePollMainContainer}>
											<EnablePollContainer
												enablePoll={enablePoll}
												setEnablePoll={setEnablePoll}
												pollOption={pollOption}
												setPollOption={setPollOption}
												setCustomPoll={setCustomPoll}
												customPoll={customPoll}
												setShowResults={setShowResults}
												showResults={showResults}
												fileData={fileData}
												isPollLocked={isPollLocked}
												// Pass current state to preserve it during navigation
												allState={{
													caption,
													fileType,
													timer,
													tag,
													commentPrivacy,
													likeViewPrivacy,
													sharingPrivacy
												}}
											/>

											{/* <div className={styles.ShowPollResult}>
										<p>Show Poll Results</p>
										<Switch
											onChange={(e) => {
												if (enablePoll) setPollResult(e);
												else toast.info("Please Enable Poll !");
											}}
											checked={pollResult}
											offColor={"#c4c4c4"}
											onColor={"#909AFF"}
											offHandleColor={"#959595"}
											onHandleColor={"#2E5EE2"}
											uncheckedIcon={false}
											checkedIcon={false}
											activeBoxShadow={"0 0 0px 0px #909AFF"}
											height={30}
											width={64}
										/>
									</div> */}
										</div>
									</div>

									{/* 👇 Make this Hidden when EnablePollContainer Enable 👇 */}
									{enablePoll && (
										<div className={styles.SetTimerContainer}>
											<p>Set Timer</p>
											<div className={styles.SetTimerInner}>
												<div className={styles.SetTimerInnerLeft}>
													<div
														className={`${styles.dates} ${timer === "1" ? styles.active : ""}`}
														onClick={() => {
															if (enablePoll) setTimer("1");
															else toast.info("Please Enable Poll !");
														}}>
														1D
													</div>
													<div
														className={`${styles.dates} ${timer === "2" ? styles.active : ""}`}
														onClick={() => {
															if (enablePoll) setTimer("2");
															else toast.info("Please Enable Poll !");
														}}>
														2D
													</div>
													<div
														className={`${styles.dates} ${timer === "3" ? styles.active : ""}`}
														onClick={() => {
															if (enablePoll) setTimer("3");
															else toast.info("Please Enable Poll !");
														}}>
														3D
													</div>
													<div
														className={`${styles.dates} ${timer === (customDays ? customDays.toString() : "7") ? styles.active : ""}`}
														onClick={() => {
															if (enablePoll) setTimer(customDays ? customDays.toString() : "7");
															else toast.info("Please Enable Poll !");
														}}>
														{customDays ? `${customDays}D` : "7D"}
													</div>
												</div>
												<div className={styles.SetTimerInnerRight}>
													<div
														onClick={() => {
															if (enablePoll) setOpenCal(true);
															else toast.info("Please Enable Poll !");
														}}
														style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "5px", cursor: "pointer", textTransform: "uppercase" }}
													>
														Set Custom
													</div>

													<CustomCalendar
														isOpen={openCal}
														setIsOpen={setOpenCal}
														setTimer={setTimer}
														setSelectedDate={setSelectedDate}
													/>

												</div>
											</div>
										</div>
									)}
									{/* 👆 Make this Hidden when EnablePollContainer Enable 👆 */}
								</div>

									<div className={styles.describeContainer}>
										<p>Describe your feeling</p>
										<div className={styles.TextAreaContainer}>
											<textarea
												ref={textRef}
												value={caption}
												placeholder="Write your caption here... (max 32 words)"
												onChange={handleCaptionChange}
												className={styles.textarea1}
											/>

											{showMentions && (
												<div className={styles.MentionsDropdown}>
													{filteredFollowers.length > 0 ? (
														filteredFollowers.map((f) => (
															<div key={f._id} className={styles.MentionItem} onClick={() => insertMention(f)}>
																<img src={f.ProfilePic} alt="" />
																<span>{f.displayName || f.username}</span>
															</div>
														))
													) : (
														<div className={styles.MentionItem}>
															<span>{isFollowersLoading ? "Loading followers..." : "No users found"}</span>
														</div>
													)}
												</div>
											)}

											<div className={styles.row2}>
												<textarea
													value={tagText}
													placeholder='Add #tags (Example: #Nature #Art)'
													onChange={(e) => {
														const newVal = e.target.value.replace(/#\s+/g, '#');
														setTagText(newVal);
														let rawTags = newVal
															.split(/\s+/)
															.filter(t => t.startsWith("#"))
															.map(t => t.replace(/^#/, ""));
														setTag(rawTags);
													}}
													className={styles.textarea2}></textarea>
												<span>
													{caption.trim() === ""
														? "00"
														: caption.trim().split(/\s+/).length.toString().padStart(2, "0")}
													/32
												</span>
												<button onClick={() => {
													navigate('/AskFameo', {
														state: {
															caption,
															fileData,
															fileType,
															enablePoll,
															customPoll,
															timer,
															tag,
															commentPrivacy,
															likeViewPrivacy,
															sharingPrivacy
														}
													})
												}} className={styles.AskFameo}>Ask Fameo</button>
											</div>
										</div>
									</div>

									<div className={styles.PostContainer}>
										<div className={styles.PostCont1}>
											<h2>Post Settings</h2>
											<div className={styles.PostCont1Inn}>
												<h5>Like & View</h5>
												<p onClick={() => {
													setShowLikeViewOptions(!showLikeViewOptions);
													setShowCommentOptions(false);
													setShowSharingOptions(false);
												}}>
													{likeViewPrivacy.option.replace('_', ' ')} {">"}
												</p>
											</div>
											<div className={styles.PostCont1Inn}>
												<h5>Allow comments from</h5>
												<p onClick={() => {
													setShowCommentOptions(!showCommentOptions);
													setShowLikeViewOptions(false);
													setShowSharingOptions(false);
												}}>
													{commentPrivacy.option.replace('_', ' ')} {">"}
												</p>
											</div>
											<div className={styles.PostCont1Inn}>
												<h5>Post sharing</h5>
												<p onClick={() => {
													setShowSharingOptions(!showSharingOptions);
													setShowCommentOptions(false);
													setShowLikeViewOptions(false);
												}}>
													{sharingPrivacy.option.replace('_', ' ')} {">"}
												</p>
											</div>
										</div>

										{(showCommentOptions || showLikeViewOptions || showSharingOptions) && (
											<div className={styles.PostCont2} style={{ height: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
												<p>Allow from ({showCommentOptions ? "Allow comments from" : showLikeViewOptions ? "Like & View" : "Post sharing"})</p>
												{[
													{ id: "everyone", label: "Everyone" },
													{ id: "followers", label: "Your Followers" },
													{ id: "followers_except", label: "Your Followers Except" },
													{ id: "no_one_except", label: "No One Except" }
												].map((opt) => (
													<div className={styles.PostCont2Inn} key={opt.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
														<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
															<h2>{opt.label}</h2>
															<Switch
																onChange={(checked) => {
																	if (checked) {
																		if (showCommentOptions) setCommentPrivacy({ ...commentPrivacy, option: opt.id });
																		else if (showLikeViewOptions) setLikeViewPrivacy({ ...likeViewPrivacy, option: opt.id });
																		else setSharingPrivacy({ ...sharingPrivacy, option: opt.id });
																	}
																}}
																checked={(showCommentOptions ? commentPrivacy.option : showLikeViewOptions ? likeViewPrivacy.option : sharingPrivacy.option) === opt.id}
																offColor={"#F2DBF1"}
																onColor={"#F2DBF1"}
																offHandleColor={"#A6539D"}
																onHandleColor={"#A6539D"}
																uncheckedIcon={false}
																checkedIcon={false}
																activeBoxShadow={"0 0 0px 0px #909AFF"}
																height={14}
																width={33}
															/>
														</div>
														{opt.id === 'followers' && (
															<p style={{ fontSize: '0.75rem', color: '#A6539D', marginTop: '4px', marginBottom: '8px', fontWeight: '500' }}>
																{followers.length} Followers
															</p>
														)}
													</div>
												))}

												{(((showCommentOptions ? commentPrivacy.option : showLikeViewOptions ? likeViewPrivacy.option : sharingPrivacy.option) === "followers_except") ||
													((showCommentOptions ? commentPrivacy.option : showLikeViewOptions ? likeViewPrivacy.option : sharingPrivacy.option) === "no_one_except")) && (
														<div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
															<div style={{
																display: 'flex',
																alignItems: 'center',
																background: '#FCF7FC',
																borderRadius: '8px',
																padding: '8px 12px',
																marginBottom: '1rem',
																border: '1px solid #eee'
															}}>
																<FiSearch style={{ marginRight: '10px', color: '#888' }} />
																<input
																	type="text"
																	placeholder="Search followers..."
																	value={searchTerm}
																	onChange={(e) => handleUserSearch(e.target.value)}
																	style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: '0.9rem' }}
																/>
															</div>
															<div className={styles.PrivacyFollowerList}>
																{(() => {
																	const listToDisplay = searchTerm.trim() === "" ? followers : searchResults;
																	if (listToDisplay.length === 0) {
																		return (
																			<div style={{ textAlign: 'center', padding: '20px', color: '#999', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
																				<IoAlertCircle style={{ fontSize: '1.5rem', marginBottom: '5px', color: '#A6539D' }} />
																				<p style={{ fontSize: '0.9rem', textTransform: 'none' }}>Follower not found</p>
																			</div>
																		);
																	}
																	return listToDisplay.map(u => {
																		const privacy = showCommentOptions ? commentPrivacy : showLikeViewOptions ? likeViewPrivacy : sharingPrivacy;
																		const setPrivacy = showCommentOptions ? setCommentPrivacy : showLikeViewOptions ? setLikeViewPrivacy : setSharingPrivacy;
																		const isSelected = privacy.specific_users.some(su => (su._id || su) === u._id);
																		const isChecked = privacy.option === "followers_except" ? !isSelected : isSelected;

																		return (
																			<div
																				key={u._id}
																				onClick={() => toggleUserInPrivacy(privacy, setPrivacy, u)}
																				style={{
																					display: 'flex',
																					justifyContent: 'space-between',
																					alignItems: 'center',
																					cursor: 'pointer',
																					padding: '10px 8px',
																					borderBottom: '1px solid #f9f9f9',
																					transition: 'background-color 0.2s',
																					borderRadius: '6px'
																				}}
																				onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fefefe'}
																				onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
																			>
																				<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
																					<Avatar src={u.ProfilePic} name={u.username} style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
																					<span style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>{u.displayName || u.username}</span>
																				</div>
																				<div
																					style={{
																						width: '20px',
																						height: '20px',
																						borderRadius: '5px',
																						border: isChecked ? 'none' : '1.5px solid #ccc',
																						display: 'flex',
																						alignItems: 'center',
																						justifyContent: 'center',
																						background: isChecked ? '#A6539D' : 'transparent',
																						transition: 'all 0.2s'
																					}}
																				>
																					{isChecked && <span style={{ color: 'white', fontSize: '13px', fontWeight: 'bold' }}>✓</span>}
																				</div>
																			</div>
																		);
																	});
																})()}
															</div>
														</div>
													)}
											</div>
										)}

										{/* <div className={styles.searchBar}>
											<div className={styles.searchInput}>
												<span className={styles.searchIcon}>
													<FiSearch />
												</span>
												<input type="text" placeholder="Search" />
											</div>
										</div> */}

									</div>

								<button className={styles.uploadBtn} disabled={btnDisable} onClick={handelUpload}>
									{btnDisable ? (
										<>
											<div>
												<FallingLines
													color="#fff"
													width="60"
													visible={true}
													ariaLabel="falling-lines-loading"
												/>
												{!isEdit && `${uploadPercentage}%`}
											</div>
											{!isEdit && <span style={{ width: `${uploadPercentage}%` }}></span>}
										</>
									) : (
										isEdit ? "Update" : "Upload"
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default UploadPost;


